import {
  PortfolioInviteRequest,
  PortfolioRoles,
  PortfolioService,
} from '@/api';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { CaretSortIcon } from '@radix-ui/react-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckIcon, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

// Define the roles dropdown
type rolesDropdown = {
  value: PortfolioRoles;
  description: string;
};
const roles: rolesDropdown[] = [
  { value: 'Viewer', description: 'Can view' },
  { value: 'Editor', description: 'Can view, create and edit' },
  { value: 'Owner', description: 'Admin-level access to all resources' },
];

// Define the form schema
const formSchema = z.object({
  email: z.string().email({
    message: 'Please enter a valid email address.',
  }),
  name: z.string().min(2, {
    message: 'Name must be at least 2 characters.',
  }),
  role: z.enum(['Viewer', 'Editor', 'Owner']),
});

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  portfolioId: string;
}

export function AddNewPortfolioCollaborator({
  open,
  setOpen,
  portfolioId,
}: Props) {
  // query client
  const queryClient = useQueryClient();

  // Define the form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      name: '',
    },
  });

  // mutation to add a new collaborator
  const portfolioAddCollaborator = useMutation({
    mutationFn: async (payload: PortfolioInviteRequest) => {
      // call the api to add a new collaborator
      const res = await PortfolioService.portfolioAddCollaborator(
        portfolioId,
        payload,
      );

      return res;
    },
    onSuccess: data => {
      // do something with the data

      // invalidate some queries here so that they are refetched
      queryClient.invalidateQueries({
        queryKey: ['getPortfolioCollaborators'],
      });

      // close the drawer
      setOpen(false);
    },
    onError: error => {
      console.error(error);
      setOpen(false);
      toast('An error occurred', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  // Define the submit handler.
  async function onSubmit(values: z.infer<typeof formSchema>) {
    // run the mutation
    portfolioAddCollaborator.mutateAsync(values);
  }

  return (
    <Sheet
      open={open}
      onOpenChange={o => {
        form.reset();
        setOpen(o);
      }}
    >
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Add new collaborator to accounts</SheetTitle>
          <SheetDescription>
            Invite your team members to collaborate in accounts.
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-4 space-y-8"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter the name of the member you want to invite.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter the email address of the member you want to invite.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Role</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          role="combobox"
                          className={cn(
                            ' justify-between',
                            !field.value && 'text-muted-foreground',
                          )}
                        >
                          {field.value
                            ? roles.find(r => r.value === field.value)?.value
                            : 'Select Role'}
                          <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="p-0">
                      <Command>
                        <CommandInput placeholder="Search Role..." />
                        <CommandEmpty>No role found.</CommandEmpty>
                        <CommandGroup>
                          {roles.map(r => (
                            <CommandItem
                              value={r.value}
                              key={r.value}
                              onSelect={() => {
                                form.setValue('role', r.value);
                              }}
                              className=""
                            >
                              <CheckIcon
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  r.value === field.value
                                    ? 'opacity-100'
                                    : 'opacity-0',
                                )}
                              />
                              <div className="flex flex-col items-start">
                                <p>{r.value}</p>
                                <p className="text-sm text-muted-foreground">
                                  {r.description}
                                </p>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <FormDescription>
                    Select the role you want to assign to the member.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SheetFooter>
              <SheetClose asChild>
                <Button variant="outline">Cancel</Button>
              </SheetClose>
              <Button
                type="submit"
                disabled={portfolioAddCollaborator.isPending}
              >
                {portfolioAddCollaborator.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Submit
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
